/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'next/router';
import { shape, arrayOf, string } from 'prop-types';
import { compose } from 'redux';
import withDynamicReducer from '@tcp/core/src/components/common/hoc/withDynamicReducer';
import withDynamicSaga from '@tcp/core/src/components/common/hoc/withDynamicSaga';
import ShopThisStoreSaga from '@tcp/core/src/components/features/browse/ShopThisStore/container/ShopThisStore.saga';
import ShopThisStoreReducer from '@tcp/core/src/components/features/browse/ShopThisStore/container/ShopThisStore.reducer';
import withStyles from '../../../../../../common/hoc/withStyles';
import errorBoundary from '../../../../../../common/hoc/withErrorBoundary';
import { crossBrandRedirect } from '../../../../../../../utils/utils.web';
import GlobalNavigationPanel from './GlobalNavigationPanel';
import { isShopThisStoreModeOn } from '../../../../../../../reduxStore/selectors/session.selectors';
import { getFilteredList } from '../../../../ShopThisStore/container/ShopThisStore.selectors';
import { fetchBreadCrumbs } from '../../../../CategoryListingPage/container/CategoryListingPage.selectors';
import { getFilteredL2AndL1Categories } from '../../../../ShopThisStore/container/ShopThisStore.actions';
import { getDefaultFavStore } from '../../../../../account/User/container/User.selectors';
/**
 * Function to generate static ids. takes two parameters prefix and baseId
 * @param {*} prefix
 * @param {*} baseId
 * @returns staticId incremeted by 1 each time
 */

export const staticIdGenerator = (prefix, baseId = 0) => {
  let initialCounter = baseId;

  return () => {
    initialCounter += 1;
    return `${prefix}-${initialCounter}`;
  };
};

const generateStaticId = staticIdGenerator('cat-group', 0);

/**
 * gets sub categories in a recursive way i.e L2, L3, L4 etc dynamically
 * @param {*} param0
 * @param {*} activeCategoryIds
 * @returns
 */

export const getSubCategories = (
  {
    categoryContent: { id = '', name = '', seoToken = '', displayToCustomer, outfitsPlp = '' } = {},
    asPath = '',
    url: toPath,
    subCategories = [],
  },
  activeCategoryIds
) => {
  return {
    id,
    name,
    displayToCustomer,
    seoToken,
    asPath: crossBrandRedirect(asPath, name, 'topmenu-'),
    toPath,
    subCategories: subCategories?.map((item) => getSubCategories(item, activeCategoryIds)) || [],
    active: activeCategoryIds?.includes(id),
    outfitsPlp,
  };
};

/**
 * function to get category groups. Caregories are grouped by these category groups
 * @param {*} categoryGroupData
 * @param {*} activeCategoryIds
 * @returns category groups which has hasSubCategory flag true and then sorted by order attribute
 * and then mapped as group details
 */

export const getGroups = (categoryGroupData = {}, activeCategoryIds = []) => {
  return Object.values(categoryGroupData)
    .filter((groupItem) => groupItem.hasSubCategory)
    .sort((a, b) => {
      return parseInt(a.order, 10) - parseInt(b.order, 10);
    })
    .map((group) => {
      return {
        groupName: group.label,
        groupId: generateStaticId(),
        subCategories:
          group.items
            ?.filter((item) => item.categoryContent.displayToCustomer)
            ?.map((item) => getSubCategories(item, activeCategoryIds)) || [],
      };
    });
};

/**
 * data transformer which transforms the raw data returned from APIs to the minimum data required by UI components
 * @param {*} param0
 * @param {*} activeCategoryIds
 * @returns transformed data
 */

export const dataTranformer = ({ subCategories = {} }, activeCategoryIds = []) => {
  return getGroups(subCategories, activeCategoryIds);
};

/**
 * container component for the presentation component. consumes the presentation component as data-driven.
 * makes use of Memoization for data transformation and rendering child component to achieve optimized performance
 * @param {*} param0
 * @returns
 */

const GlobalNavigationPanelContainer = ({
  navigationTree = {},
  activeCategoryIds = [],
  newFilterTrackAnalytics,
  isOutfitPLPEnabled,
  isSTSModeOn,
  filterList,
  breadCrumbs,
  getL2AndL1Categories,
  defaultStore,
  router,
}) => {
  const groups = useMemo(() => {
    return dataTranformer(navigationTree, activeCategoryIds);
  }, [navigationTree, activeCategoryIds]);

  const MemoizedNavigationPanel = useMemo(() => {
    return (
      <GlobalNavigationPanel
        groups={groups}
        config={{
          categoryCap: 4,
          labels: {
            showMore: 'Show more',
            showLess: 'Show less',
          },
        }}
        newFilterTrackAnalytics={newFilterTrackAnalytics}
        isOutfitPLPEnabled={isOutfitPLPEnabled}
        isShopThisStoreModeOn={isSTSModeOn}
        filterList={filterList}
        breadCrumbs={breadCrumbs}
        getL2AndL1Categories={getL2AndL1Categories}
        defaultStore={defaultStore}
        router={router}
      />
    );
  }, [groups, newFilterTrackAnalytics, isOutfitPLPEnabled, filterList]);

  return <>{MemoizedNavigationPanel}</>;
};

GlobalNavigationPanelContainer.propTypes = {
  navigationTree: shape({}).isRequired,
  activeCategoryIds: arrayOf(string).isRequired,
};

const mapStateToProps = (state) => {
  return {
    isSTSModeOn: isShopThisStoreModeOn(),
    filterList: getFilteredList(state),
    breadCrumbs: fetchBreadCrumbs(state),
    defaultStore: getDefaultFavStore(state),
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    getL2AndL1Categories: (storeId, breadCrumbs) => {
      dispatch(getFilteredL2AndL1Categories(storeId, breadCrumbs));
    },
  };
};
const withReducer = withDynamicReducer({ key: 'ShopThisStore', reducer: ShopThisStoreReducer });
const withSaga = withDynamicSaga({ key: 'ShopThisStoreSaga', saga: ShopThisStoreSaga });
const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default withRouter(
  compose(
    withReducer,
    withSaga,
    withConnect
  )(withStyles(errorBoundary(GlobalNavigationPanelContainer)))
);
